/**
 * /inventory/lost-items — packer loss reports and their approval queue.
 *
 * The spine is `order.LostItem`: a packer scans what is physically on the shelf,
 * anything expected but unscanned is reported lost, and an approver decides.
 * Approval is what actually writes the loss off stock, so the pending queue is
 * a backlog of un-booked shrinkage, not just paperwork.
 *
 * Tabs own their own queries; Radix only mounts the active one, so opening the
 * page costs a single request.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { PackageX, TriangleAlert, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import { BarChart } from '@/components/charts/BarChart';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { openLostItemDetail } from '@/state/lostItemDetailDialog';
import { rangeISO, useFilters } from '@/state/filters';
import {
  LOST_ITEM_REASON_LABEL,
  LOST_ITEM_STATUS_TONE,
  lostItemImageUrl,
  useLostItems,
  useLostItemsAttribution,
  useLostItemsOverview,
  type LostItem,
  type LostItemReason,
  type LostItemStatus,
} from '@/api/bi/lostItems';
import { fmt } from '@/lib/format';

type StatusFilter = LostItemStatus | 'decided' | 'all';
type ReasonFilter = LostItemReason | 'all';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'decided', label: 'Decided' },
  { value: 'all', label: 'All statuses' },
];

const REASON_OPTIONS: Array<{ value: ReasonFilter; label: string }> = [
  { value: 'all', label: 'All reasons' },
  { value: 'not_available', label: 'Not Available' },
  { value: 'partial_missing', label: 'Partial Missing' },
  { value: 'other', label: 'Other' },
];

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function numParam(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * A filter that came from the URL rather than a visible control. Without this
 * the list would look like the whole queue while quietly showing one slice.
 */
function UrlFilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/30">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label} filter`}
        className="press rounded-full p-0.5 hover:bg-primary/20"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

interface TabProps {
  filters: { start: string; end: string; storeId: number | null };
}

export function LostItemsPage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const filters = { start: iso.start, end: iso.end, storeId: storeId ?? null };

  return (
    <>
      <PageHeader
        eyebrow="Inventory"
        title="Lost items"
        description="Units reported missing at pick time. Approving a report writes the net missing quantity off stock; rejecting restores it."
      />

      <Tabs defaultValue="queue">
        <TabsList className="mb-3">
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="attribution">Attribution</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
          <QueueTab filters={filters} />
        </TabsContent>
        <TabsContent value="overview">
          <OverviewTab filters={filters} />
        </TabsContent>
        <TabsContent value="attribution">
          <AttributionTab filters={filters} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

function ProductCell({ row }: { row: LostItem }) {
  const thumb = lostItemImageUrl(row.product_image);
  return (
    <div className="flex items-center gap-2">
      {thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          className="h-8 w-8 shrink-0 rounded border border-border object-cover"
        />
      ) : (
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded border border-border text-muted-foreground">
          <PackageX className="h-3.5 w-3.5" />
        </div>
      )}
      <span className="line-clamp-2 max-w-[220px] leading-tight">
        {row.product_name ?? `Product #${row.product_id ?? '—'}`}
      </span>
    </div>
  );
}

/** Reported / found / net, mirroring how the write-off quantity is derived. */
function QuantityCell({ row }: { row: LostItem }) {
  return (
    <div className="text-right">
      <div className="font-medium">{fmt.number(row.net_units)}</div>
      <div className="text-[10px] text-muted-foreground">
        {row.quantity} lost · {row.found_quantity} found
      </div>
    </div>
  );
}

function ActorCell({
  name,
  at,
}: {
  name: string | null | undefined;
  at: string | null | undefined;
}) {
  if (!name && !at) return <span className="text-muted-foreground">—</span>;
  return (
    <div>
      <div>{name ?? '—'}</div>
      <div className="text-[10px] text-muted-foreground">{fmt.datetime(at)}</div>
    </div>
  );
}

function QueueTab({ filters }: TabProps) {
  // Cross-links from the audit drill-downs arrive with `product_id` or
  // `reported_by` set, so the queue seeds from the URL rather than ignoring it.
  const [searchParams, setSearchParams] = useSearchParams();
  const productId = numParam(searchParams.get('product_id'));
  const reportedBy = numParam(searchParams.get('reported_by'));
  const urlStatus = searchParams.get('approval_status') as StatusFilter | null;

  const [status, setStatus] = useState<StatusFilter>(
    urlStatus && STATUS_OPTIONS.some((o) => o.value === urlStatus) ? urlStatus : 'pending',
  );
  const [reason, setReason] = useState<ReasonFilter>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  function clearUrlFilter(key: 'product_id' | 'reported_by') {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  const query = useLostItems({
    ...filters,
    approvalStatus: status,
    reason,
    productId,
    reportedBy,
    q: debouncedQ || null,
    page,
    pageSize,
  });
  const rows = query.data?.results ?? [];
  const pagination = query.data?.pagination;
  const totals = query.data?.totals;

  const columns = useMemo<ColumnDef<LostItem, unknown>[]>(
    () => [
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => <ProductCell row={row.original} />,
      },
      {
        id: 'quantity',
        header: () => <div className="text-right">Net / Reported</div>,
        cell: ({ row }) => <QuantityCell row={row.original} />,
      },
      {
        id: 'order',
        header: 'Order',
        cell: ({ row }) =>
          row.original.order_id != null ? (
            <Link
              to={`/orders/${row.original.order_id}`}
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              #{row.original.order_id}
            </Link>
          ) : (
            <span className="text-muted-foreground">Floating</span>
          ),
      },
      {
        id: 'store',
        header: 'Store',
        cell: ({ row }) => row.original.store?.name ?? '—',
      },
      {
        id: 'reported_by',
        header: 'Reported by',
        cell: ({ row }) => (
          <ActorCell
            name={row.original.reported_by?.name}
            at={row.original.reported_at}
          />
        ),
      },
      {
        id: 'approved_by',
        header: 'Approved by',
        cell: ({ row }) => (
          <ActorCell
            name={row.original.approved_by?.name}
            at={row.original.approved_at}
          />
        ),
      },
      {
        id: 'reason',
        header: 'Reason',
        cell: ({ row }) =>
          LOST_ITEM_REASON_LABEL[row.original.reason] ?? row.original.reason,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={LOST_ITEM_STATUS_TONE[row.original.approval_status]}>
            {row.original.approval_status}
          </Badge>
        ),
      },
      {
        id: 'value',
        header: () => <div className="text-right">Est. loss</div>,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.estimated_value != null
              ? fmt.npr(row.original.estimated_value)
              : '—'}
          </div>
        ),
      },
      {
        id: 'written_off',
        header: 'Written off',
        cell: ({ row }) =>
          row.original.applied_to_stock ? (
            <Badge tone="success">Yes</Badge>
          ) : row.original.approval_status === 'approved' ? (
            // Approved but not debited never reaches the ledger, so the loss is
            // invisible in Count-vs-Book until someone notices.
            <Badge tone="danger">
              <TriangleAlert className="h-3 w-3" />
              No
            </Badge>
          ) : (
            <Badge tone="neutral">No</Badge>
          ),
      },
    ],
    [],
  );

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  return (
    <DataView
      data={rows}
      columns={columns}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      onRowClick={(row) =>
        openLostItemDetail({ lostItemId: row.id, productName: row.product_name })
      }
      search={{
        value: q,
        onChange: resetPage(setQ),
        placeholder: 'Search product, order, reporter…',
      }}
      toolbar={
        <div className="flex flex-wrap items-center gap-2">
          {productId != null ? (
            <UrlFilterChip
              label={`Product #${productId}`}
              onClear={() => clearUrlFilter('product_id')}
            />
          ) : null}
          {reportedBy != null ? (
            <UrlFilterChip
              label={`Reporter #${reportedBy}`}
              onClear={() => clearUrlFilter('reported_by')}
            />
          ) : null}
          <Select
            value={status}
            onValueChange={(v) => resetPage(setStatus)(v as StatusFilter)}
          >
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={reason}
            onValueChange={(v) => resetPage(setReason)(v as ReasonFilter)}
          >
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REASON_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
      pagination={
        pagination
          ? {
              page: pagination.page,
              pageSize: pagination.page_size,
              total: pagination.total,
              onPageChange: setPage,
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }
          : undefined
      }
      footer={
        totals ? (
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span>
              Net missing in filter:{' '}
              <span className="font-medium text-foreground">
                {fmt.number(totals.net_units)}
              </span>{' '}
              units
            </span>
            <span>
              Reported: <span className="text-foreground">{fmt.number(totals.units)}</span>
            </span>
            <span>
              Pending: <span className="text-foreground">{fmt.number(totals.pending)}</span>
            </span>
          </div>
        ) : undefined
      }
      emptyTitle="No lost-item reports"
      emptyDescription={
        debouncedQ
          ? 'Nothing matches this search. Try another product, order, or reporter.'
          : 'No reports in this window for the selected filters.'
      }
      maxHeight="68vh"
      stickyFirstColumn
      columnToggles
    />
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab({ filters }: TabProps) {
  const query = useLostItemsOverview(filters);

  if (query.isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <ErrorState
        description="Could not load the lost-item overview."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const d = query.data;
  const k = d.kpis;
  const aging = d.pending_aging;
  const hygieneTotal =
    d.hygiene.approved_not_applied +
    d.hygiene.rejected_still_applied +
    d.hygiene.pending_already_applied;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard
          label="Pending approvals"
          value={fmt.number(k.pending_total)}
          hint={`${fmt.number(k.pending_net_units)} units un-booked`}
        />
        <KPICard
          label="Approval rate"
          value={pct(k.approval_rate)}
          hint={`${k.approved} approved / ${k.rejected} rejected`}
        />
        <KPICard
          label="Median decision time"
          value={
            k.median_approval_hours != null ? `${k.median_approval_hours}h` : '—'
          }
          hint="Reported to decided"
        />
        <KPICard
          label="Net units lost"
          value={fmt.number(k.net_units)}
          hint={`${fmt.number(k.reports)} reports · ${fmt.number(k.skus)} SKUs`}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Pending age</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={[
                { name: '< 1 day', value: aging['0_1d'] },
                { name: '1–3 days', value: aging['1_3d'] },
                { name: '3–7 days', value: aging['3_7d'] },
                { name: '7 days +', value: aging['7d_plus'] },
              ]}
              xKey="name"
              yKey="value"
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Reason mix</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={d.by_reason.map((r) => ({
                name: LOST_ITEM_REASON_LABEL[r.reason] ?? r.reason,
                value: r.net_units,
              }))}
              xKey="name"
              yKey="value"
              horizontal
            />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Net units lost by day</CardTitle>
        </CardHeader>
        <CardBody>
          <BarChart
            data={d.by_day.map((r) => ({ name: r.day ?? '—', value: r.net_units }))}
            xKey="name"
            yKey="value"
          />
        </CardBody>
      </Card>

      {hygieneTotal > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Write-off hygiene</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              A decision and its stock movement should always agree. These rows
              disagree, so their loss is either missing from the ledger or
              double-counted in it.
            </p>
            <ul className="space-y-1">
              {d.hygiene.approved_not_applied > 0 ? (
                <li>
                  <Badge tone="danger">{d.hygiene.approved_not_applied}</Badge>{' '}
                  approved but never written off stock
                </li>
              ) : null}
              {d.hygiene.rejected_still_applied > 0 ? (
                <li>
                  <Badge tone="danger">{d.hygiene.rejected_still_applied}</Badge>{' '}
                  rejected but stock still debited
                </li>
              ) : null}
              {d.hygiene.pending_already_applied > 0 ? (
                <li>
                  <Badge tone="warning">{d.hygiene.pending_already_applied}</Badge>{' '}
                  still pending but stock already debited — expected for floating
                  reports, which debit at report time rather than on approval
                </li>
              ) : null}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By store</CardTitle>
          </CardHeader>
          <CardBody className="overflow-x-auto p-0">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Store</th>
                  <th className="px-4 py-2 text-right">Reports</th>
                  <th className="px-4 py-2 text-right">Net units</th>
                  <th className="px-4 py-2 text-right">Pending</th>
                </tr>
              </thead>
              <tbody>
                {d.by_store.map((r) => (
                  <tr key={r.store_id ?? r.store_name} className="border-t border-border">
                    <td className="px-4 py-2">{r.store_name}</td>
                    <td className="px-4 py-2 text-right">{r.reports}</td>
                    <td className="px-4 py-2 text-right">{r.net_units}</td>
                    <td className="px-4 py-2 text-right">{r.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Top SKUs</CardTitle>
          </CardHeader>
          <CardBody className="overflow-x-auto p-0">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Reports</th>
                  <th className="px-4 py-2 text-right">Net units</th>
                  <th className="px-4 py-2 text-right">Est. loss</th>
                </tr>
              </thead>
              <tbody>
                {d.top_skus.map((r) => (
                  <tr key={r.product_id ?? r.product_name} className="border-t border-border">
                    <td className="px-4 py-2">{r.product_name ?? '—'}</td>
                    <td className="px-4 py-2 text-right">{r.reports}</td>
                    <td className="px-4 py-2 text-right">{r.net_units}</td>
                    <td className="px-4 py-2 text-right">
                      {r.estimated_value != null ? fmt.npr(r.estimated_value) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

function AttributionTab({ filters }: TabProps) {
  const query = useLostItemsAttribution(filters);

  if (query.isLoading) return <Skeleton className="h-64" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        description="Could not load attribution."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const d = query.data;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{d.caveat}</p>
      <Card>
        <CardHeader>
          <CardTitle>Reporters</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-right">Reports</th>
                <th className="px-4 py-2 text-right">Net units</th>
                <th className="px-4 py-2 text-right">SKUs</th>
                <th className="px-4 py-2 text-right">Approved</th>
                <th className="px-4 py-2 text-right">Rejected</th>
                <th className="px-4 py-2 text-right">Pending</th>
                <th className="px-4 py-2 text-right">Rejection rate</th>
              </tr>
            </thead>
            <tbody>
              {d.reporters.map((r) => (
                <tr key={r.user_id ?? r.user_name} className="border-t border-border">
                  <td className="px-4 py-2">{r.user_name}</td>
                  <td className="px-4 py-2 text-right">{r.reports}</td>
                  <td className="px-4 py-2 text-right">{r.net_units}</td>
                  <td className="px-4 py-2 text-right">{r.skus}</td>
                  <td className="px-4 py-2 text-right">{r.approved}</td>
                  <td className="px-4 py-2 text-right">{r.rejected}</td>
                  <td className="px-4 py-2 text-right">{r.pending}</td>
                  <td className="px-4 py-2 text-right">{pct(r.rejection_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Approvers</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-right">Decisions</th>
                <th className="px-4 py-2 text-right">Approved</th>
                <th className="px-4 py-2 text-right">Rejected</th>
                <th className="px-4 py-2 text-right">Net units</th>
                <th className="px-4 py-2 text-right">Approval rate</th>
              </tr>
            </thead>
            <tbody>
              {d.approvers.map((r) => (
                <tr key={r.user_id ?? r.user_name} className="border-t border-border">
                  <td className="px-4 py-2">{r.user_name}</td>
                  <td className="px-4 py-2 text-right">{r.decisions}</td>
                  <td className="px-4 py-2 text-right">{r.approved}</td>
                  <td className="px-4 py-2 text-right">{r.rejected}</td>
                  <td className="px-4 py-2 text-right">{r.net_units}</td>
                  <td className="px-4 py-2 text-right">{pct(r.approval_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
